import {
  type FC, useCallback, useEffect,
} from 'react';
import { useDispatch } from 'react-redux';
import { type PolygonPath } from '~/_shared/types/polygon/polygon.types';
import { createUuid } from '~/_shared/utils/createUuid';
import { LassoDrawComponent } from '~/map/lassoSelect/lassoDraw.component';
import { pathToMapShapeOutlines } from '~/map/map/mapObjects/mapShape/mapShapeModel';
import { useSnapToShapes } from '~/map/map/mapObjects/mapShape/private/useSnapToShapes.hook';
import { useBoundaryDrawPolygonZIndex } from '~/map/zIndexes/useBoundaryDrawPolygonZIndex.hook';
import {
  boundaryDrawAddShape, boundaryDrawDeactivate, boundaryDrawOutlineHover, boundaryDrawOutlineMouseOut,
  boundaryDrawStartDrawing, boundaryDrawStopDrawing,
} from '~/store/frontendState/mapTools/boundary/boundaryDraw/boundaryDraw.actionCreators';
import { useBoundaryDrawActiveSelector } from '~/store/frontendState/mapTools/boundary/boundaryDraw/boundaryDraw.selectors';
import { DrawType } from '~/store/frontendState/mapTools/boundary/boundaryDraw/boundaryDraw.state';
import { useBoundaryDrawSnapTargets } from './useBoundaryDrawSnapTargets.hook';

export const BoundaryDrawContainer: FC = () => {
  const dispatch = useDispatch();

  const drawState = useBoundaryDrawActiveSelector();
  const zIndex = useBoundaryDrawPolygonZIndex();
  const snapTargets = useBoundaryDrawSnapTargets(drawState?.snapEnabled ? drawState : null);
  const snap = useSnapToShapes(snapTargets);

  const addShape = useCallback((path: PolygonPath) => {
    const shape = { id: createUuid(), outlines: pathToMapShapeOutlines(path) };

    dispatch(boundaryDrawAddShape(shape));
  }, [dispatch]);

  const stopDrawing = useCallback(() => {
    dispatch(boundaryDrawDeactivate());
  }, [dispatch]);

  const onOutlineHover = useCallback(() => dispatch(boundaryDrawOutlineHover()), [dispatch]);
  const onOutlineMouseOut = useCallback(() => dispatch(boundaryDrawOutlineMouseOut()), [dispatch]);

  const isDrawing = drawState?.drawType === DrawType.Draw;

  useEffect(() => {
    if (isDrawing) {
      dispatch(boundaryDrawStartDrawing());
    }

    return () => {
      if (isDrawing) {
        dispatch(boundaryDrawStopDrawing());
      }
    };
  }, [dispatch, isDrawing]);

  if (!isDrawing) {
    return null;
  }

  return (
    <LassoDrawComponent
      onDrawingFinished={addShape}
      onDrawingStop={stopDrawing}
      onFirstOutlineHover={onOutlineHover}
      onFirstOutlineMouseOut={onOutlineMouseOut}
      snap={snap}
      zIndex={zIndex}
    />
  );
};
